/* ==========================================================================
   Madson Golf — theme.js
   No libraries. Transform/opacity only. prefers-reduced-motion respected.
   Modules: MGMotion (scroll reveal + signature magnet snap + parallax),
            focus trap, announcement bar, header (search, mobile nav, cart count).
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     MGMotion — ported verbatim from the approved prototype's motion.js
     (Level 2.5: playful, performant, one signature)
     ------------------------------------------------------------------------ */
  function reveal(root) {
    if (reduced) return;
    var nodes = (root || document).querySelectorAll('[data-mg-reveal]:not([data-mg-done])');
    if (!nodes.length) return;
    var seeded = [];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target;
        var delay = parseInt(el.getAttribute('data-mg-delay') || '0', 10);
        setTimeout(function () {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0px)';
        }, delay);
        io.unobserve(el);
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -40px 0px' });
    nodes.forEach(function (el) {
      el.setAttribute('data-mg-done', '1');
      var r = el.getBoundingClientRect();
      if (r.top > window.innerHeight * 0.92) {
        el.style.opacity = '0';
        el.style.transform = 'translateY(18px)';
        el.style.transition = 'opacity 480ms cubic-bezier(0.22,0.61,0.36,1), transform 480ms cubic-bezier(0.22,0.61,0.36,1)';
        el.style.willChange = 'opacity, transform';
        io.observe(el);
        seeded.push(el);
      }
    });
    // Failsafe: content must never stay invisible if the observer misses
    // (fast flings, odd IO timing on mobile Safari). Checks once a second
    // until every seeded element has revealed, then stops.
    if (seeded.length) {
      var guard = setInterval(function () {
        seeded = seeded.filter(function (el) {
          if (el.style.opacity !== '0') return false;
          if (el.getBoundingClientRect().top < window.innerHeight) {
            el.style.opacity = '1';
            el.style.transform = 'translateY(0px)';
            return false;
          }
          return true;
        });
        if (!seeded.length) clearInterval(guard);
      }, 1000);
    }
  }

  // The signature: magnet snap. Element starts lifted away, springs onto its target when in view.
  function snap(root) {
    var nodes = (root || document).querySelectorAll('[data-mg-snap]:not([data-mg-done])');
    if (!nodes.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target;
        var delay = parseInt(el.getAttribute('data-mg-delay') || '150', 10);
        setTimeout(function () {
          el.style.transform = 'translate(0px, 0px) rotate(0deg)';
          el.style.opacity = '1';
        }, delay);
        io.unobserve(el);
      });
    }, { threshold: 0.5 });
    nodes.forEach(function (el) {
      el.setAttribute('data-mg-done', '1');
      if (reduced) { el.style.opacity = '1'; el.style.transform = 'none'; return; }
      var from = el.getAttribute('data-mg-snap') || '-56,-12,-8';
      var parts = from.split(',').map(Number);
      el.style.opacity = '0.9';
      el.style.transform = 'translate(' + parts[0] + 'px,' + parts[1] + 'px) rotate(' + parts[2] + 'deg)';
      el.style.transition = 'transform 600ms cubic-bezier(0.34,1.56,0.64,1), opacity 200ms linear';
      el.style.willChange = 'transform';
      io.observe(el);
    });
  }

  // Subtle parallax on desert imagery (desktop only, rAF-throttled, transform-only)
  var parallaxBound = false;
  function parallax() {
    if (reduced || window.innerWidth < 900) return;
    var nodes = Array.prototype.slice.call(document.querySelectorAll('[data-mg-parallax]'));
    if (!nodes.length) return;
    var ticking = false;
    function frame() {
      ticking = false;
      var vh = window.innerHeight;
      nodes.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-mg-parallax') || '0.08');
        var r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        var centerDelta = (r.top + r.height / 2) - vh / 2;
        el.style.transform = 'translateY(' + (-centerDelta * speed).toFixed(1) + 'px) scale(1.06)';
      });
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }
    if (!parallaxBound) {
      window.addEventListener('scroll', onScroll, { passive: true });
      parallaxBound = true;
    }
    frame();
  }

  function initMotion(root) { reveal(root); snap(root); parallax(); }

  window.MGMotion = { reveal: reveal, snap: snap, parallax: parallax, init: initMotion, reduced: reduced };

  /* ------------------------------------------------------------------------
     Focus trap — for drawers/dialogs (a11y: focus stays inside while open)
     ------------------------------------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container) {
    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      var items = Array.prototype.filter.call(
        container.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
    container.addEventListener('keydown', onKeydown);
    return function release() { container.removeEventListener('keydown', onKeydown); };
  }

  window.MGA11y = { trapFocus: trapFocus, FOCUSABLE: FOCUSABLE };

  /* ------------------------------------------------------------------------
     Announcement bar — rotates messages, dismissible, remembers state
     ------------------------------------------------------------------------ */
  function initAnnouncement(scope) {
    var bar = (scope || document).querySelector('[data-mg-announce]');
    if (!bar || bar.dataset.mgInit) return;
    bar.dataset.mgInit = '1';

    var storageKey = bar.getAttribute('data-storage-key');
    try {
      if (storageKey && localStorage.getItem(storageKey) === 'dismissed') {
        bar.hidden = true;
        return;
      }
    } catch (e) { /* storage unavailable — keep showing */ }

    var messages = [];
    try { messages = JSON.parse(bar.getAttribute('data-messages') || '[]'); } catch (e) { /* noop */ }
    var msgEl = bar.querySelector('[data-announce-msg]');
    var idx = 0;

    if (messages.length > 1 && msgEl && !reduced) {
      setInterval(function () {
        msgEl.classList.add('is-fading');
        setTimeout(function () {
          idx = (idx + 1) % messages.length;
          msgEl.textContent = messages[idx];
          msgEl.classList.remove('is-fading');
        }, 260);
      }, parseInt(bar.getAttribute('data-interval') || '4000', 10));
    }

    var close = bar.querySelector('[data-announce-close]');
    if (close) {
      close.addEventListener('click', function () {
        bar.hidden = true;
        try { if (storageKey) localStorage.setItem(storageKey, 'dismissed'); } catch (e) { /* noop */ }
      });
    }
  }

  /* ------------------------------------------------------------------------
     Header — search toggle, mobile nav drawer, live cart count
     ------------------------------------------------------------------------ */
  function initHeader(scope) {
    var header = (scope || document).querySelector('[data-mg-header]');
    if (!header || header.dataset.mgInit) return;
    header.dataset.mgInit = '1';

    // Desktop search toggle
    var searchToggle = header.querySelector('[data-search-toggle]');
    var searchForm = header.querySelector('[data-search-form]');
    if (searchToggle && searchForm) {
      searchToggle.addEventListener('click', function () {
        var open = searchForm.classList.toggle('is-open');
        searchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          var input = searchForm.querySelector('input[type="search"]');
          if (input) input.focus();
        }
      });
      searchForm.addEventListener('focusout', function () {
        setTimeout(function () {
          if (!searchForm.contains(document.activeElement) && document.activeElement !== searchToggle) {
            searchForm.classList.remove('is-open');
            searchToggle.setAttribute('aria-expanded', 'false');
          }
        }, 150);
      });
    }

    // Mobile nav drawer
    var drawer = document.querySelector('[data-mg-nav-drawer]');
    var burger = header.querySelector('[data-nav-open]');
    if (drawer && burger) {
      var releaseTrap = null;
      var lastFocus = null;

      function openNav() {
        lastFocus = document.activeElement;
        drawer.classList.add('is-open');
        drawer.removeAttribute('aria-hidden');
        burger.setAttribute('aria-expanded', 'true');
        document.body.classList.add('mg-scroll-lock');
        releaseTrap = trapFocus(drawer);
        var firstClose = drawer.querySelector('[data-nav-close]');
        if (firstClose) firstClose.focus();
        document.addEventListener('keydown', onEsc);
      }
      function closeNav() {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        burger.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('mg-scroll-lock');
        if (releaseTrap) { releaseTrap(); releaseTrap = null; }
        document.removeEventListener('keydown', onEsc);
        if (lastFocus) lastFocus.focus();
      }
      function onEsc(e) { if (e.key === 'Escape') closeNav(); }

      burger.addEventListener('click', openNav);
      drawer.querySelectorAll('[data-nav-close]').forEach(function (el) {
        el.addEventListener('click', closeNav);
      });
    }

    // Cart button: open the drawer when it exists (Stage 7), otherwise /cart link default
    var cartBtn = header.querySelector('[data-cart-open]');
    if (cartBtn) {
      cartBtn.addEventListener('click', function (e) {
        if (document.querySelector('[data-mg-cart-drawer]')) {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('madson:cart-open'));
        }
      });
    }
  }

  // Live cart count (all headers on page; updates on madson:cart-changed)
  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = !(count > 0);
    });
  }
  window.addEventListener('madson:cart-changed', function (e) {
    if (e.detail && typeof e.detail.count === 'number') setCartCount(e.detail.count);
  });
  window.MGCart = {
    refreshCount: function () {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { setCartCount(cart.item_count); })
        .catch(function () { /* offline — keep server-rendered count */ });
    }
  };

  /* ------------------------------------------------------------------------
     Boot + Shopify theme editor support
     ------------------------------------------------------------------------ */
  function initAll(scope) {
    initAnnouncement(scope);
    initHeader(scope);
    initMotion(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();

/* ==========================================================================
   Product page — gallery, variant switching, qty, ATC feedback, sticky bar,
   related-products fetch. Progressive: without JS the form still submits.
   ========================================================================== */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    var withThousands = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',').replace(/\.00$/, '');
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, withThousands)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/<[^>]+>/g, '').trim() || '$' + amount;
  }

  function initProduct(scope) {
    var root = (scope || document).querySelector('[data-mg-product]');
    if (!root || root.dataset.mgInit) return;
    root.dataset.mgInit = '1';

    var dataEl = root.querySelector('[data-product-json]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var form = document.getElementById('ProductForm');
    var idInput = root.querySelector('[data-variant-id]');
    var priceEl = root.querySelector('[data-price]');
    var atcBtn = root.querySelector('[data-atc]');
    var atcRow = root.querySelector('[data-atc-row]');
    var express = root.querySelector('[data-express]');
    var bis = root.querySelector('[data-bis]');
    var bisForm = root.querySelector('[data-bis-form]');
    var qtyInput = root.querySelector('[data-qty-input]');
    var qtyValue = root.querySelector('[data-qty-value]');
    var sticky = document.querySelector('[data-sticky-atc]');
    var stickyPrice = sticky && sticky.querySelector('[data-sticky-price]');
    var stickyImg = sticky && sticky.querySelector('[data-sticky-image]');
    var stickyBtn = sticky && sticky.querySelector('[data-sticky-add]');

    var selected = data.variants.find(function (v) { return String(v.id) === String(idInput && idInput.value); }) || data.variants[0];
    var qty = 1;
    var atcState = 'idle';

    /* ---- Gallery ---- */
    var strip = root.querySelector('[data-gallery-strip]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-thumb]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-dot]'));

    function setSlide(i, scrollIntoView) {
      slides.forEach(function (s, j) { s.classList.toggle('is-active', j === i); });
      thumbs.forEach(function (t, j) { t.setAttribute('aria-current', j === i ? 'true' : 'false'); });
      dots.forEach(function (d, j) { d.setAttribute('aria-current', j === i ? 'true' : 'false'); });
      if (scrollIntoView && strip && window.innerWidth < 900) {
        strip.scrollTo({ left: slides[i].offsetLeft, behavior: 'smooth' });
      }
    }
    thumbs.forEach(function (t, i) { t.addEventListener('click', function () { setSlide(i, true); }); });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { setSlide(i, true); }); });
    if (strip && slides.length > 1) {
      var scrollT;
      strip.addEventListener('scroll', function () {
        clearTimeout(scrollT);
        scrollT = setTimeout(function () {
          var i = Math.round(strip.scrollLeft / strip.clientWidth);
          dots.forEach(function (d, j) { d.setAttribute('aria-current', j === i ? 'true' : 'false'); });
        }, 80);
      }, { passive: true });
    }

    function showVariantMedia(variant) {
      if (!variant.mediaId) return;
      var i = slides.findIndex(function (s) { return String(s.getAttribute('data-media-id')) === String(variant.mediaId); });
      if (i >= 0) setSlide(i, true);
    }

    /* ---- Variant selection ---- */
    var optionButtons = Array.prototype.slice.call(root.querySelectorAll('[data-option-value]'));
    var selectedOptions = selected ? selected.options.slice() : [];

    function findVariant() {
      return data.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === selectedOptions[i]; });
      });
    }

    var atcLabelEl = atcBtn && (atcBtn.querySelector('[data-atc-label]') || atcBtn);
    function updateAtcLabel() {
      if (!atcBtn || !selected) return;
      if (atcState !== 'idle') return;
      var tpl = atcBtn.getAttribute('data-label-template') || 'Add to cart — __PRICE__';
      atcLabelEl.textContent = tpl.replace('__PRICE__', formatMoney(selected.price * qty, data.moneyFormat));
    }

    function applyVariant(variant) {
      if (!variant) return;
      selected = variant;
      if (idInput) idInput.value = variant.id;
      if (priceEl) {
        var html = formatMoney(variant.price, data.moneyFormat);
        if (variant.compareAt > variant.price) html += ' <s>' + formatMoney(variant.compareAt, data.moneyFormat) + '</s>';
        priceEl.innerHTML = html;
      }
      var available = variant.available;
      if (atcRow) atcRow.hidden = !available;
      if (express) express.hidden = !available;
      if (bis) bis.hidden = available;
      if (bisForm) bisForm.setAttribute('data-bis-variant', variant.id);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, data.moneyFormat);
      if (stickyImg && variant.image) stickyImg.src = variant.image;
      if (stickyBtn) stickyBtn.disabled = !available;
      Array.prototype.forEach.call(document.querySelectorAll('[data-atc-proxy]'), function (b) { b.hidden = !available; });
      optionButtons.forEach(function (b) {
        var i = parseInt(b.getAttribute('data-option-index'), 10);
        b.setAttribute('aria-pressed', selectedOptions[i] === b.getAttribute('data-value') ? 'true' : 'false');
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-option-label]'), function (el) {
        var i = parseInt(el.getAttribute('data-option-label'), 10);
        el.textContent = selectedOptions[i];
      });
      updateAtcLabel();
      showVariantMedia(variant);
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      } catch (e) { /* noop */ }
    }

    optionButtons.forEach(function (b) {
      b.addEventListener('click', function () {
        var i = parseInt(b.getAttribute('data-option-index'), 10);
        selectedOptions[i] = b.getAttribute('data-value');
        var v = findVariant();
        if (v) applyVariant(v);
      });
    });

    /* ---- Quantity ---- */
    function setQty(n) {
      qty = Math.max(1, Math.min(9, n));
      if (qtyInput) qtyInput.value = qty;
      if (qtyValue) qtyValue.textContent = qty;
      updateAtcLabel();
    }
    var dec = root.querySelector('[data-qty-dec]');
    var inc = root.querySelector('[data-qty-inc]');
    if (dec) dec.addEventListener('click', function () { setQty(qty - 1); });
    if (inc) inc.addEventListener('click', function () { setQty(qty + 1); });

    /* ---- Add to cart (fetch; falls back to normal submit without JS) ---- */
    function addToCart() {
      if (atcState !== 'idle' || !selected || !selected.available) return;
      atcState = 'adding';
      if (atcBtn) atcLabelEl.textContent = atcBtn.getAttribute('data-label-adding') || 'Adding…';
      var body = { id: selected.id, quantity: qty, properties: {}, sections: 'cart-drawer' };
      var giftToggle = root.querySelector('[data-gift-toggle]');
      if (giftToggle && giftToggle.checked) {
        body.properties.Gift = 'Yes';
      }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function (item) {
          atcState = 'added';
          var addedSections = item && item.sections;
          if (atcBtn) {
            atcLabelEl.textContent = atcBtn.getAttribute('data-label-added') || 'Added. Nice choice.';
            atcBtn.classList.add('is-added');
          }
          if (stickyBtn) stickyBtn.classList.add('is-added');
          if (window.MGCart) window.MGCart.refreshCount();
          window.dispatchEvent(new CustomEvent('madson:cart-added', { detail: { sections: addedSections } }));
          setTimeout(function () {
            atcState = 'idle';
            if (atcBtn) atcBtn.classList.remove('is-added');
            if (stickyBtn) stickyBtn.classList.remove('is-added');
            setQty(1);
          }, 1800);
        })
        .catch(function () {
          atcState = 'idle';
          if (form) form.submit();
        });
    }
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        addToCart();
      });
    }
    if (stickyBtn) stickyBtn.addEventListener('click', addToCart);

    /* ---- Back in stock (Klaviyo client API) ----
       BIS subscription only — no list in the payload, so the double-opt-in
       Email List is never bypassed. Variant rides along as Shopify's
       catalog-variant id ($shopify:::$default:::<variant_id>). */
    if (bisForm) {
      bisForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var emailInput = bisForm.querySelector('input[type="email"]');
        var bisBtn = bisForm.querySelector('button[type="submit"]');
        var bisFields = bisForm.querySelector('[data-bis-fields]');
        var bisOk = bisForm.querySelector('[data-bis-success]');
        var bisErr = bisForm.querySelector('[data-bis-error]');
        if (!emailInput || !emailInput.value) return;
        bisErr.hidden = true;
        bisBtn.disabled = true;
        fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(bisForm.getAttribute('data-bis-company')), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/vnd.api+json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email: emailInput.value.trim() } } }
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + bisForm.getAttribute('data-bis-variant') } }
              }
            }
          })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('bis failed');
            bisFields.hidden = true;
            bisOk.hidden = false;
            bisBtn.disabled = false;
          })
          .catch(function () {
            bisErr.hidden = false;
            bisBtn.disabled = false;
          });
      });
    }

    /* ---- Sticky bar past 720px scroll ---- */
    if (sticky) {
      var shown = false;
      window.addEventListener('scroll', function () {
        var show = window.scrollY > 720;
        if (show !== shown) { shown = show; sticky.hidden = !show; }
      }, { passive: true });
    }

    updateAtcLabel();
  }

  /* ---- Related products (recommendations API) ---- */
  function initRelated(scope) {
    var el = (scope || document).querySelector('[data-related-products]');
    if (!el || el.dataset.mgInit) return;
    el.dataset.mgInit = '1';
    var url = el.getAttribute('data-url');
    if (!url) return;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-related-products] .mg-prelated__grid');
        if (fresh && fresh.querySelector('.mg-card')) {
          el.querySelector('.mg-prelated__grid').innerHTML = fresh.innerHTML;
        }
      })
      .catch(function () { /* demo grid stays */ });
  }

  function boot(scope) { initProduct(scope); initRelated(scope); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();

/* ==========================================================================
   Collection page — sort select + faceted filter drawer
   ========================================================================== */
(function () {
  'use strict';

  function initCollection(scope) {
    var bar = (scope || document).querySelector('[data-mg-collection]');
    if (!bar || bar.dataset.mgInit) return;
    bar.dataset.mgInit = '1';

    var sortForm = bar.querySelector('[data-sort-form]');
    var sortSelect = bar.querySelector('[data-sort-select]');
    if (sortForm && sortSelect) {
      sortSelect.addEventListener('change', function () { sortForm.submit(); });
    }

    var drawer = document.querySelector('[data-filter-drawer]');
    var openBtn = bar.querySelector('[data-filter-open]');
    if (drawer && openBtn) {
      var release = null;
      var lastFocus = null;
      function open() {
        lastFocus = document.activeElement;
        drawer.classList.add('is-open');
        drawer.removeAttribute('aria-hidden');
        openBtn.setAttribute('aria-expanded', 'true');
        document.body.classList.add('mg-scroll-lock');
        if (window.MGA11y) release = window.MGA11y.trapFocus(drawer);
        var closeBtn = drawer.querySelector('.mg-icon-btn[data-filter-close]');
        if (closeBtn) closeBtn.focus();
        document.addEventListener('keydown', onEsc);
      }
      function close() {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        openBtn.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('mg-scroll-lock');
        if (release) { release(); release = null; }
        document.removeEventListener('keydown', onEsc);
        if (lastFocus) lastFocus.focus();
      }
      function onEsc(e) { if (e.key === 'Escape') close(); }
      openBtn.addEventListener('click', open);
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-filter-close]'), function (el) {
        el.addEventListener('click', close);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initCollection(document); });
  } else {
    initCollection(document);
  }
  document.addEventListener('shopify:section:load', function (e) { initCollection(e.target); });
})();

/* ==========================================================================
   Cart drawer — open/close, live line updates via /cart/change.js, section
   re-render via the Section Rendering API, quick-add interception.
   Event delegation on the drawer root survives content swaps.
   ========================================================================== */
(function () {
  'use strict';

  var drawer = null;
  var releaseTrap = null;
  var lastFocus = null;
  var busy = false;

  function getDrawer() {
    if (!drawer) drawer = document.querySelector('[data-mg-cart-drawer]');
    return drawer;
  }

  function onEsc(e) { if (e.key === 'Escape') closeDrawer(); }

  function openDrawer() {
    var d = getDrawer();
    if (!d || d.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    d.classList.add('is-open');
    d.removeAttribute('aria-hidden');
    document.body.classList.add('mg-scroll-lock');
    if (window.MGA11y) releaseTrap = window.MGA11y.trapFocus(d);
    var close = d.querySelector('.mg-icon-btn[data-cart-close]');
    if (close) close.focus();
    document.addEventListener('keydown', onEsc);
  }

  function closeDrawer() {
    var d = getDrawer();
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mg-scroll-lock');
    if (releaseTrap) { releaseTrap(); releaseTrap = null; }
    document.removeEventListener('keydown', onEsc);
    if (lastFocus) lastFocus.focus();
  }

  function applySections(sections) {
    var html = sections && sections['cart-drawer'];
    if (!html) return false;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-drawer-content]');
    var current = getDrawer() && getDrawer().querySelector('[data-drawer-content]');
    if (fresh && current) {
      current.innerHTML = fresh.innerHTML;
      if (window.MGCart) window.MGCart.refreshCount();
      return true;
    }
    return false;
  }

  function refreshDrawer() {
    return fetch(window.location.pathname + '?sections=cart-drawer', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections['cart-drawer'];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-drawer-content]');
        var current = getDrawer() && getDrawer().querySelector('[data-drawer-content]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        if (window.MGCart) window.MGCart.refreshCount();
      })
      .catch(function () { /* keep stale drawer; count refresh still runs on next change */ });
  }

  function changeLine(line, quantity) {
    if (busy) return;
    busy = true;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity, sections: 'cart-drawer' })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!applySections(cart.sections)) return refreshDrawer();
      })
      .then(function () { busy = false; })
      .catch(function () { busy = false; });
  }

  function addVariant(variantId) {
    if (busy) return;
    busy = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1, sections: 'cart-drawer' })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function (item) {
        if (!applySections(item.sections)) return refreshDrawer();
      })
      .then(function () { busy = false; openDrawer(); })
      .catch(function () { busy = false; });
  }

  /* Delegated clicks inside the drawer (survive innerHTML swaps) */
  document.addEventListener('click', function (e) {
    var d = getDrawer();
    if (!d) return;
    var t = e.target;

    var closeEl = t.closest('[data-cart-close]');
    if (closeEl && d.contains(closeEl)) {
      if (closeEl.tagName === 'A') { closeDrawer(); return; }
      e.preventDefault();
      closeDrawer();
      return;
    }
    if (!d.contains(t)) return;

    var dec = t.closest('[data-line-dec]');
    if (dec) {
      var line = parseInt(dec.getAttribute('data-line-dec'), 10);
      var qtyEl = dec.parentElement.querySelector('span');
      var q = parseInt(qtyEl ? qtyEl.textContent : '1', 10);
      changeLine(line, Math.max(0, q - 1));
      return;
    }
    var inc = t.closest('[data-line-inc]');
    if (inc) {
      changeLine(parseInt(inc.getAttribute('data-line-inc'), 10), parseInt(inc.getAttribute('data-qty'), 10) + 1);
      return;
    }
    var rem = t.closest('[data-line-remove]');
    if (rem) {
      changeLine(parseInt(rem.getAttribute('data-line-remove'), 10), 0);
      return;
    }
    var cross = t.closest('[data-cross-add]');
    if (cross) {
      addVariant(cross.getAttribute('data-cross-add'));
      return;
    }
  });

  /* Quick-add forms (product cards): intercept and add via fetch.
     The PDP form (#ProductForm) has its own handler — skip it. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches('form[action*="/cart/add"]') || form.id === 'ProductForm') return;
    var idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;
    e.preventDefault();
    addVariant(idInput.value);
  });

  /* Global events. cart-added may carry the freshly rendered section
     (bundled with the add mutation — same request, same session). */
  window.addEventListener('madson:cart-open', function () { refreshDrawer().then(openDrawer); });
  window.addEventListener('madson:cart-added', function (e) {
    if (e.detail && e.detail.sections && applySections(e.detail.sections)) { openDrawer(); return; }
    refreshDrawer().then(openDrawer);
  });
})();

/* ==========================================================================
   Magnet Tech page — "Replay the snap"
   ========================================================================== */
(function () {
  'use strict';
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-snap-replay]');
    if (!btn) return;
    var el = document.getElementById(btn.getAttribute('data-snap-replay'));
    if (!el) return;
    if (window.MGMotion && window.MGMotion.reduced) return;
    var from = (el.getAttribute('data-mg-snap') || '-120,-160,-14').split(',').map(Number);
    el.style.transition = 'none';
    el.style.transform = 'translate(' + from[0] + 'px,' + from[1] + 'px) rotate(' + from[2] + 'deg)';
    el.style.opacity = '0.9';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.style.transition = 'transform 600ms cubic-bezier(0.34,1.56,0.64,1), opacity 200ms linear';
        el.style.transform = 'translate(0px, 0px) rotate(0deg)';
        el.style.opacity = '1';
      });
    });
  });
})();

/* ==========================================================================
   Inline Add to cart CTAs (materials + gifting sections) — submit the ONE
   product form (respects qty, opens drawer via the existing add flow).
   Fallback: scroll to the buy box (instant under prefers-reduced-motion).
   ========================================================================== */
(function () {
  'use strict';
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-atc-proxy]');
    if (!btn) return;
    var form = document.getElementById('ProductForm');
    var mainAtc = form && form.querySelector('[data-atc]');
    if (form && mainAtc && !mainAtc.disabled && typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else if (form && mainAtc) {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    } else {
      var buy = document.querySelector('.mg-pdp__buy');
      if (buy) {
        var reduced = window.MGMotion && window.MGMotion.reduced;
        buy.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      }
    }
  });
})();

/* ==========================================================================
   Judge.me preview badge — jump to the review widget on click/Enter.
   (Judge.me's own scroll binding lives in its interactive bundle, which its
   v3 loader doesn't always fetch; this keeps the badge useful regardless.)
   ========================================================================== */
(function () {
  'use strict';
  function jump() {
    var target = document.getElementById('reviews');
    if (!target) return;
    var reduced = window.MGMotion && window.MGMotion.reduced;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest('.jdgm-preview-badge')) jump();
  });
  document.addEventListener('keyup', function (e) {
    if (e.key === 'Enter' && e.target.closest && e.target.closest('.jdgm-preview-badge')) jump();
  });
})();
